import { OptionPreview } from './../store/getters';
import { snapchat } from '@/gen/proto/protos';
import IAssetsV3 = snapchat.bitmoji.api.IAssetsV3;
import IFacialFeatureCategory = snapchat.bitmoji.api.IFacialFeatureCategory;
import IFacialFeatureMetadata = snapchat.bitmoji.api.IFacialFeatureMetadata;
import Context = snapchat.bitmoji.api.FacialFeatureCategory.Context;
import { FacialFeatureColourPickerConfig } from '@/services/ColourPickerOptionsHelper';

const EARRING_PREFIX = 'earring_';
const FACIAL_FEATURE_CATEGORY_KEYS: string[] = [
  'earring_dual',
  'earring_left',
  'earring_right',
  'nosering',
  'browring_left',
  'browring_right',
  'earaid_left',
  'earaid_right',
  'mouthring',
  'tonguering',
];
const FACIAL_FEATURE_HEARING_AID_CATEGORY_KEYS: string[] = ['earaid_left', 'earaid_right'];
const FACIAL_FEATURE_EARRING_CATEGORY_KEYS: string[] = [
  'earring_dual',
  'earring_left',
  'earring_right',
];
const FACIAL_FEATURE_CATEGORY_FROM_CONTEXT_KEYS: any[] = [
  'earring_dual',
  {
    earring: 'earring_left',
    browring: 'browring_left',
    earaid: 'earaid_left',
  },
  {
    earring: 'earring_right',
    browring: 'browring_right',
    earaid: 'earaid_right',
  },
  {
    nosering: 'nosering',
    mouthring: 'mouthring',
    tonguering: 'tonguering',
  },
];
const EARRING_LEFT_SLOTS_KEYS = [
  'earringL_cartilage1',
  'earringL_cartilage2',
  'earringL_cartilage3',
  'earringL_industrial1',
  'earringL_lobe1',
  'earringL_lobe2',
  'earringL_tragus1',
];
const EARRING_RIGHT_SLOTS_KEYS = [
  'earringR_cartilage1',
  'earringR_cartilage2',
  'earringR_cartilage3',
  'earringR_industrial1',
  'earringR_lobe1',
  'earringR_lobe2',
  'earringR_tragus1',
];
const NOSERING_SLOT_KEYS = [
  'nosering_bridge',
  'nosering_nostrilHighL',
  'nosering_nostrilHighR',
  'nosering_nostrilL',
  'nosering_nostrilR',
  'nosering_septum',
];

const BROWRING_LEFT_SLOTS_KEYS = [
  'browringL_ABottom',
  'browringL_ARing',
  'browringL_ATop',
  'browringL_BBottom',
  'browringL_BRing',
  'browringL_BTop',
  'browringL_CBottom',
  'browringL_CTop',
  'browringL_DBottom',
  'browringL_DTop',
];

const BROWRING_RIGHT_SLOTS_KEYS = [
  'browringR_ABottom',
  'browringR_ARing',
  'browringR_ATop',
  'browringR_BBottom',
  'browringR_BRing',
  'browringR_BTop',
  'browringR_CBottom',
  'browringR_CTop',
  'browringR_DBottom',
  'browringR_DTop',
];

const EARAID_LEFT_SLOTS_KEYS = ['earringL_hearingAid'];

const EARAID_RIGHT_SLOTS_KEYS = ['earringR_hearingAid'];

const MOUTHRING_SLOTS_KEYS = [
  'mouthring_bottomRing1R',
  'mouthring_bottomRing2R',
  'mouthring_bottomRing1L',
  'mouthring_bottomRing2L',
  'mouthring_bottomRingC1',
  'mouthring_bottomStudR',
  'mouthring_bottomStudL',
  'mouthring_bottomStudCenter',
  'mouthring_topR',
  'mouthring_topL',
  'mouthring_topCenter',
];

const TONGUERING_SLOTS_KEYS = ['tonguering_mid', 'tonguering_tip'];

const VITAL_MAGIC_COLOURS_COUNT: Record<string, number> = {
  earring_dual: 4,
  earring_left: 4,
  earring_right: 4,
  nosering: 2,
  browring_left: 4,
  browring_right: 4,
  earaid_left: 4,
  earaid_right: 4,
  mouthring: 4,
  tonguering: 4,
};

const FACIAL_FEATURE_CONTEXTS = [Context.DUAL, Context.LEFT, Context.RIGHT, Context.SINGLE];

/**
 * getKeyFromContext: translate the constant Context into a string key
 * @param context
 * @returns
 */
const getKeyFromContext = (context: Context, category: string | null | undefined): string => {
  if (
    (context === Context.SINGLE || context === Context.LEFT || context === Context.RIGHT) &&
    category
  ) {
    return FACIAL_FEATURE_CATEGORY_FROM_CONTEXT_KEYS[FACIAL_FEATURE_CONTEXTS.indexOf(context)][
      category
    ];
  }
  return FACIAL_FEATURE_CATEGORY_KEYS[FACIAL_FEATURE_CONTEXTS.indexOf(context)];
};

/**
 * getFacialFeaturePreviewDisplayType gives the display type needed to show the option preview
 * @param key
 * @returns
 */
const getFacialFeaturePreviewDisplayType = (key: string) => {
  if (
    key === 'earring-left' ||
    key === 'earring-right' ||
    key === 'nosering' ||
    key === 'browring-left' ||
    key === 'browring-right' ||
    key === 'mouthring' ||
    key === 'tonguering'
  ) {
    return [key];
  } else if (key === 'earaid-left') {
    return ['earring-left'];
  } else if (key === 'earaid-right') {
    return ['earring-right'];
  } else if (key === 'earring-dual') {
    return ['earring-left', 'earring-left']; // when we do option preview, we flip a earring left.
    // this is due to the asymmetry of the left and right ear in the preview.
  }
  throw new Error(`[FacialFeaturesHelper] ${key} does not have a display type`);
};

/**
 * getFacialFeaturePreviewQueryParameterRoot gives the roots needed to send a query for the
 * main preview or to specify trait keys used to save avatar data (e.g. state.selectedTraits)
 *
 * @param key
 * @returns
 */
const getFacialFeaturePreviewQueryParameterRoot = (key: string) => {
  if (key === 'earring_left' || key === 'earaid_left') {
    return ['earringL'];
  } else if (key === 'earring_right' || key === 'earaid_right') {
    return ['earringR'];
  } else if (key === 'earring_dual') {
    return ['earringR', 'earringL'];
  } else if (key === 'nosering') {
    return ['nosering'];
  } else if (key === 'browring_left') {
    return ['browringL'];
  } else if (key === 'browring_right') {
    return ['browringR'];
  } else if (key === 'mouthring') {
    return ['mouthring'];
  } else if (key === 'tonguering') {
    return ['tonguering'];
  }
  throw new Error(`[FacialFeaturesHelper] ${key} does a corresponding query parameter`);
};

/**
 * getFacialFeatureCategoryUniqueRoot gives roots for a given facial feature category similarly to
 * {@link getFacialFeaturePreviewQueryParameterRoot}. The exception is that this function returns
 * a distinct root for each category as displayed in the builder. This distinction is important for
 * facial feature categories that appear in separate builder categories but share preview/avatar data keys
 * (e.g. 'earring*' vs 'earaid*' which both preview/save as 'earring*' keys).
 *
 * @param key
 */
const getFacialFeatureCategoryUniqueRoot = (key: string) => {
  // Earring and ear aid builder categories differ from their avatar data keys.
  if (key === 'earring_left') {
    return ['earringL'];
  } else if (key === 'earring_right') {
    return ['earringR'];
  } else if (key === 'earaid_left') {
    return ['earaidL'];
  } else if (key === 'earaid_right') {
    return ['earaidR'];
  }
  // The other facial features categories do not differ from their avatar data keys.
  return getFacialFeaturePreviewQueryParameterRoot(key);
};

/**
 * getRootKeysToDelete gives the roots of the keys needed to be removed from state.selectedTraits
 * @param category
 * @returns
 */
const getRootKeysToDelete = (category: string): string[] => {
  return isHearingAidCategory(category)
    ? getHearingAidRootKeysToDelete(category)
    : getFacialFeaturePreviewQueryParameterRoot(category);
};

/**
 * getHearingAidKeysToDelete gives the the keys needed to be removed from state.selectedTraits
 * special case use for hearing aids categories
 * @param category
 * @returns
 */
const getHearingAidRootKeysToDelete = (category: string): string[] => {
  // These are the specific trait keys roots for hearing aids which exist as special slots
  // within 'earring*' categories
  if (category === 'earaid_left') {
    return ['earringL_hearingAid'];
  }
  return ['earringR_hearingAid'];
};

const isHearingAidCategory = (category: string) =>
  FACIAL_FEATURE_HEARING_AID_CATEGORY_KEYS.includes(category);

const isEarringCategory = (category: string) =>
  FACIAL_FEATURE_EARRING_CATEGORY_KEYS.includes(category);

/**
 * getCategoryTraitKeys gives the keys needed to be removed from state.selectedTraits ie. it gives all the relevant values in traits that
 * correspond to the category
 * @param category
 * @param traits
 * @returns
 */
const getCategoryTraitKeys = (category: string, traits: Record<string, number>): string[] => {
  const rootKeysToDelete = getRootKeysToDelete(category);
  let valuesToDelete: string[] = [];

  Object.keys(traits).forEach((trait) => {
    for (const rootKey of rootKeysToDelete) {
      if (trait.startsWith(rootKey)) {
        valuesToDelete.push(trait);
      }
    }
  });

  // If we're in earrings, we need to filter out keys with the special hearing aid slot to ensure
  // they are not inadvertently included (this is because the "earring*" root key will match against
  // "earring[R|L]_hearingAid" as well)
  if (isEarringCategory(category)) {
    valuesToDelete = valuesToDelete.filter((trait) => !trait.includes('hearingAid'));
  }

  return valuesToDelete;
};

/**
 * traitColourKey is used to help index state.facialFeatureTraits to get the colour of the associated group.
  facialFeatureTraits['category_groupid'] contains the colour associated with the groupId.
  traitColourKey generates the key 'category_groupid'
 * @param category
 * @param groupId
 * @returns
 */
const traitsColourKey = (category: string, groupId: string): string => {
  return `${category}_${groupId}`;
};

/**
 * getFacialFeatureTraits gives the key to value assignments needed when changing/deleting
 * state.facialFeatureTraits
 * @param category
 * @param groupId
 * @param colour
 * @returns
 */
const getFacialFeatureTraits = (category: string, groupId: string, colour: string) => {
  const rootKeys = getFacialFeatureCategoryUniqueRoot(category);
  const values: Record<string, string> = {};

  for (const root of rootKeys) {
    // every category has only 1 facial feature chosen
    values[root] = groupId;

    // every facial feature has a colour chosen. The colour shows in the option preview.
    //  The colour needs to also be preserved if the user taps away and then taps back to
    //  the facial feature.
    values[traitsColourKey(root, groupId)] = colour;
  }

  return values;
};

/**
 *  getSelectedGroupIdForCategory gets the groupIds selected for that particular category.
  for trivial cases (ie. earring_left, earring_right) it is just a data access.
  For more difficult cases, like earring_dual, we check that the groupId applies
  to all of its subsidiaries (earring_left, earring_right)
 * @param category
 * @param facialFeatureTraits
 * @returns
 */
const getSelectedGroupIdForCategory = (
  category: string,
  facialFeatureTraits: Record<string, string>,
): string | null => {
  const roots = getFacialFeatureCategoryUniqueRoot(category);

  const groupIds: string[] = [];
  for (const root of roots) {
    groupIds.push(facialFeatureTraits[root]);
  }

  if (groupIds.every((v) => v && v === groupIds[0])) {
    // all elements in groupIds must be the same. If they're not, then
    //  this category cannot apply.
    return groupIds[0];
  }
  return null;
};

/**
 * getSelectedColourForGroup gets the colourIds selected for that particular groupId.
  for trivial cases (ie. earring_left, earring_right) it is just a data access.
  For more difficult cases, it needs to check that the colours for both groupIds
  are the same.
 *
 * @param category
 * @param groupId
 * @param facialFeatureTraits
 * @returns
 */
const getSelectedColourForGroup = (
  category: string,
  groupId: string,
  facialFeatureTraits: Record<string, string>,
): string | null => {
  const roots = getFacialFeatureCategoryUniqueRoot(category);
  if (
    roots.every(
      (r) =>
        facialFeatureTraits[traitsColourKey(r, groupId)] ===
        facialFeatureTraits[traitsColourKey(roots[0], groupId)],
    )
  ) {
    return facialFeatureTraits[traitsColourKey(roots[0], groupId)];
  }
  return null;
};

/**
 * colourPickerConfigForSelectedItem gives the colour options of the selected groupId for the selected category.
 *
 * @param metadata
 * @param category
 * @param facialFeatureTraits
 * @returns
 */
const colourPickerConfigForSelectedItem = (
  metadata: IFacialFeatureMetadata,
  category: string,
  facialFeatureTraits: Record<string, string>,
): FacialFeatureColourPickerConfig | null => {
  if (!FACIAL_FEATURE_CATEGORY_KEYS.includes(category)) {
    return null;
  }

  const groupId = getSelectedGroupIdForCategory(category, facialFeatureTraits);
  if (!groupId) {
    return null;
  }
  const colourOptions = metadata?.groups?.[groupId]?.colour_options;
  if (!colourOptions) {
    return null;
  }

  // return a list of the colour ids. None of these ids can be used in queries.
  const groupColourOptionIds = colourOptions.map((v) => v?.id).filter((v) => v) as string[];
  return new FacialFeatureColourPickerConfig(groupColourOptionIds);
};

/**
 * getDefaultColourFromGroup gives the default colour for the specific group.
 * every group should technically have a default colour.
 * @param metadata
 * @param groupID
 * @returns
 */
const getDefaultColourFromGroup = (metadata: IFacialFeatureMetadata, groupID: string): string => {
  const group = metadata?.groups?.[groupID];
  if (!group || !group.default_colour_id) {
    return '';
  }

  return group.default_colour_id;
};

/**
 * getFacialFeaturePreviewQueryParameters gives the query parameters needed to attach
 * to state.selectedTraits
 *
 * @param metadata
 * @param key
 * @param groupID
 * @param colour
 * @returns
 */
const getFacialFeaturePreviewQueryParameters = (
  metadata: IFacialFeatureMetadata,
  key: string,
  groupID: string,
  colour: string,
): Record<string, number> => {
  const newTraits: Record<string, number> = {};
  const group = metadata?.groups?.[groupID];
  if (!group || !group.slots) {
    return {}; // TODO: should this throw an error?
  }

  for (const rootFeature of getFacialFeaturePreviewQueryParameterRoot(key)) {
    for (const slot of Object.keys(group.slots)) {
      newTraits[`${rootFeature}_${slot}`] = group.slots[slot];
      const colourOption = group.colour_options?.find(
        (colour_option) => colour_option.id === colour,
      );
      const tones = colourOption?.colours?.[slot]?.value;
      if (!tones) {
        continue;
      }
      // Only set the vital colours.
      // Earrings only need 4 magic colours nose rings only need 2 magic colours
      // if there's no special rule, default magic colour is 10
      const lastIndex = VITAL_MAGIC_COLOURS_COUNT[key] ? VITAL_MAGIC_COLOURS_COUNT[key] : 10;
      for (let i = 0; i < lastIndex; i++) {
        if (!tones[i]) {
          break;
        }
        newTraits[`${rootFeature}_${slot}_tone${i + 1}`] = tones[i];
      }
    }
  }
  return newTraits;
};

/**
 * getPiercingCategoriesDataFromAssets attaches earring categories to state so that they can be displayed
 * in the avatar builder
 * @param gender
 * @param style
 * @param assets
 * @returns
 */
const getPiercingCategoriesDataFromAssets = (
  gender: GenderType,
  style: StyleType,
  assets: IAssetsV3,
): FacialFeatureCategoriesData[] => {
  const facialFeatureCategoriesData: FacialFeatureCategoriesData[] = [];
  const categories: IFacialFeatureCategory[] | null | undefined =
    assets?.facial_features?.[gender]?.[style]?.categories;
  if (!categories) {
    // if (style === CategoryAssets.styleIntToString(StyleEnum.CM)) {
    //   captureMessage(
    //     `[FacialFeaturesHelper] no facial features assets available for gender (${gender}) and style (${style})`,
    //   );
    // }
    return [];
  }

  FACIAL_FEATURE_CATEGORY_KEYS.forEach(function(key: string) {
    const category: IFacialFeatureCategory | undefined = categories.find(function(category) {
      if (category.context) {
        return key === getKeyFromContext(category.context, category.key);
      }
      return false;
    });
    if (!category) {
      return;
    }
    if (category.options) {
      facialFeatureCategoriesData.push({
        key,
        trait_category_options: category.options,
      });
    }
  });

  return facialFeatureCategoriesData;
};

/**
 * optionPreviewArgs gives display types for the associated category.
 *
 * It handles the special case for earring_dual. Specifically, previewing earring_dual is a combination of
 * earring_left and a flipped earring_left.
 * @param category
 * @param value
 * @param previewType
 * @param rotation
 * @returns
 */
const optionPreviewArgs = (
  category: string,
  value: string,
  previewType: string,
  rotation: number,
): OptionPreview[] => {
  if (FACIAL_FEATURE_CATEGORY_KEYS.includes(category)) {
    const displayTypes = getFacialFeaturePreviewDisplayType(previewType);
    const args = [];

    for (const displayType of displayTypes) {
      args.push({
        category,
        value: Number(value),
        previewType: displayType,
        rotation: rotation, // rotations correspond with category_details.
        // For earring_dual, a rotation of 1 is chosen because it uses an earring_left (rotation 1)
        //  + a flipped earring_left (of rotation 1) to preview.
      });
    }
    return args;
  }
  return [];
};

/**
 * getSavedColourIdOrDefault gets the colour that has already been selected for the associated category and value (groupid).
 * It will return the default colour if there is no colour
 * @param category
 * @param value
 * @param facialFeatureTraits
 * @param facialFeaturesMetadata
 * @returns
 */

const getSavedColourIdOrDefault = (
  category: string,
  value: string,
  facialFeatureTraits: Record<string, string>,
  facialFeaturesMetadata: Record<string, string>,
): string => {
  const colour = getSelectedColourForGroup(category, value, facialFeatureTraits);
  if (colour) {
    return colour;
  }
  return getDefaultColourFromGroup(facialFeaturesMetadata, value);
};

/**
 * numberofPiercingsWearing gets how many earrings the user is wearing.
 * @param facialFeatureTraits
 * @param earringCategory
 * @returns
 */

const numberofPiercingsWearing = (
  facialFeatureTraits: Record<string, string>,
  earringCategory: string,
): Record<string, number> => {
  let count = {
    earring_left: 0,
    earring_right: 0,
    earring_dual: 0,
    nosering: 0,
    browring_left: 0,
    browring_right: 0,
    earaid_left: 0,
    earaid_right: 0,
    mouthring: 0,
    tonguering: 0,
  };

  EARRING_LEFT_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['earring_left'] += 1;
    }
  });

  EARRING_RIGHT_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['earring_right'] += 1;
    }
  });

  NOSERING_SLOT_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['nosering'] += 1;
    }
  });

  BROWRING_LEFT_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['browring_left'] += 1;
    }
  });

  BROWRING_RIGHT_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['browring_right'] += 1;
    }
  });

  EARAID_LEFT_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['earaid_left'] += 1;
    }
  });

  EARAID_RIGHT_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['earaid_right'] += 1;
    }
  });

  MOUTHRING_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['mouthring'] += 1;
    }
  });

  TONGUERING_SLOTS_KEYS.forEach((key) => {
    if (facialFeatureTraits[key]) {
      count['tonguering'] += 1;
    }
  });

  count['earring_dual'] = count['earring_left'] + count['earring_right'];
  return count;
};

export default {
  getPiercingCategoriesDataFromAssets,
  FACIAL_FEATURE_CATEGORY_KEYS,
  getFacialFeaturePreviewQueryParameters,
  getFacialFeaturePreviewDisplayType,
  optionPreviewArgs,
  getKeyFromContext,
  getFacialFeatureTraits,
  colourPickerConfigForSelectedItem,
  getSavedColourIdOrDefault,
  getSelectedGroupIdForCategory,
  getSelectedColourForGroup,
  getCategoryTraitKeys,
  numberofPiercingsWearing,
};
